import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../App";
import axios from "axios";
import { API } from "../App";
import { Calendar, CheckCircle, ArrowRight, Shield } from "lucide-react";
import { toast } from "sonner";

const Permisos = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [linked, setLinked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    let redirected = false;
    const check = async () => {
      try {
        const res = await axios.get(`${API}/auth/google-calendar/status`, { withCredentials: true });
        const isLinked = res.data?.linked ?? false;
        if (isLinked) {
          redirected = true;
          navigate("/general", { replace: true });
          return;
        }
        setLinked(false);
      } catch {
        setLinked(false);
      } finally {
        if (!redirected) setLoading(false);
      }
    };
    check();
  }, [navigate]);

  useEffect(() => {
    const error = searchParams.get("error");
    const calendarLinked = searchParams.get("calendar_linked");
    if (error === "missing_params" || error === "invalid_state") {
      toast.error("Enlace caducado o inválido. Intenta conectar de nuevo.");
    } else if (error === "token_exchange" || error === "no_refresh_token") {
      toast.error("No se pudo vincular el calendario. Intenta de nuevo.");
    } else if (calendarLinked === "1") {
      toast.success("Google Calendar conectado correctamente");
      setLinked(true);
    }
  }, [searchParams]);

  const handleConnectCalendar = async () => {
    setConnecting(true);
    try {
      const res = await axios.get(`${API}/auth/google-calendar/url`, { withCredentials: true });
      if (res.data?.url) {
        window.location.href = res.data.url;
        return;
      }
      toast.error("No se pudo obtener la URL de autorización");
    } catch (e) {
      if (e.response?.status === 503) {
        toast.error("La vinculación con Google Calendar no está configurada.");
      } else {
        toast.error(e.response?.data?.detail || "Error al conectar");
      }
    } finally {
      setConnecting(false);
    }
  };

  const handleContinue = () => {
    navigate("/general", { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-primary-50/30 to-secondary-50/30">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-slate-50 via-primary-50/30 to-secondary-50/30">
      <div className="glass-panel p-6 sm:p-10 max-w-md w-full">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
            <Shield size={28} className="text-primary" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-800">Permisos</h1>
            <p className="text-slate-500 text-sm">Vincula servicios para una mejor experiencia</p>
          </div>
        </div>

        {linked ? (
          <>
            <div className="flex items-center gap-3 p-4 rounded-xl bg-emerald-50 border border-emerald-200 mb-6">
              <CheckCircle size={24} className="text-emerald-600 flex-shrink-0" />
              <div>
                <p className="font-medium text-emerald-800">Google Calendar conectado</p>
                <p className="text-sm text-emerald-700">Podrás añadir eventos del portal a tu calendario.</p>
              </div>
            </div>
            <button onClick={handleContinue} className="btn-primary w-full flex items-center justify-center gap-2">
              Continuar a la plataforma
              <ArrowRight size={18} />
            </button>
          </>
        ) : (
          <>
            <div className="space-y-4 mb-6">
              <p className="text-slate-600 text-sm">
                Para poder añadir eventos desde el calendario de la plataforma a tu Google Calendar, necesitamos tu permiso una vez.
              </p>
              <div className="flex items-start gap-3 p-4 rounded-xl bg-slate-50 border border-slate-100">
                <Calendar size={22} className="text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-slate-800">Google Calendar</p>
                  <p className="text-sm text-slate-600">
                    Solo crearemos eventos cuando tú pulses "Añadir a mi Google Calendar". No accedemos a tu calendario sin tu acción.
                  </p>
                </div>
              </div>
            </div>
            <button
              onClick={handleConnectCalendar}
              disabled={connecting}
              className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <Calendar size={18} />
              {connecting ? "Redirigiendo..." : "Conectar Google Calendar"}
            </button>
            <button
              onClick={handleContinue}
              className="w-full mt-3 text-sm text-slate-500 hover:text-slate-700 py-2"
            >
              Continuar sin conectar
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default Permisos;
